/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';
import { CustomFieldService } from 'app/core/services/custom-field-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';

@autoinject()
export class CustomFieldChoicesGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected customFieldDefinitionId: string | null = null;

    // To load necessary user defined field definition information
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected customFieldDefinition: Core.CustomFieldDefinition | null = null;
    // Lookup choices to display on the grid
    protected choices: Core.CustomFieldChoice[] = [];

    protected editedChoice: Core.CustomFieldChoice | null = null;

    protected validate = validate;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $choiceModal!: IPopup;
    protected $choiceForm!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly customFieldService: CustomFieldService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected async bind(): Promise<void> {
        if(!this.customFieldDefinitionId) {
            throw new Error('Bindable property [custom-field-definition-id] was not set at binding');
        }

        if(!this.customFieldDefinition) {
            await this.loadCustomFieldDefinitionInformation(this.customFieldDefinitionId);
        }

        await this.reload();
    }


    ////****************** Events ***************************************************////
    protected customFieldDefinitionIdChanged(): void {
        this.reload();
    }

    public async reload(customFieldDefinitionIdChanged: boolean = false): Promise<void> {
        if(this.customFieldDefinitionId) {
            if(customFieldDefinitionIdChanged && (!this.customFieldDefinition || this.customFieldDefinitionId !== this.customFieldDefinition?.id)) {
                await this.loadCustomFieldDefinitionInformation(this.customFieldDefinitionId);
            }
            if(this.customFieldDefinitionId !== this.customFieldDefinition?.id) { this.includeInactive = false; }

            await this.loadCustomFieldChoices(this.customFieldDefinitionId);
        }
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;

        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    // #region Save Choice Modal Events
    protected handleChoiceSaved(): void {
        this.closeSaveChoiceModal();
        this.reload();
    }

    protected handleChoiceCancel(): void {
        this.closeSaveChoiceModal();
    }
    // #endregion Save Choice Modal Events


    ////****************** Functions ************************************************////
    protected saveChoice(choice: Core.CustomFieldChoice): void {
        this.editedChoice = choice;
        this.$choiceModal.open();
    }

    private closeSaveChoiceModal(): void {
        this.$choiceModal.close();
        if(this.$choiceForm.reset) {
            this.$choiceForm.reset();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCustomFieldDefinitionInformation(definitionId: string): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.customFieldService.loadCustomFieldDefinition(definitionId);
            this.customFieldDefinition = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    // Load Custom Field Lookups/Choices
    @apiCall
    private async loadCustomFieldChoices(definitionId: string): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.customFieldService.loadCustomFieldChoicesByDefinitionId(definitionId, options, []);
            this.choices = response.results;
            this.resultCount = response.count;

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateChoice(choice: Core.CustomFieldChoice): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(choice.isActive) {
            const result = await confirmation.showDeactivate('core:userFieldReference.lookup.$', 1);
            if(result.dialogResponse !== confirmation.options.OK) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.customFieldService.activateCustomFieldChoice(choice.id!, !choice.isActive);
            isSuccessful = response.isSuccessful;

            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('core:userFieldReference.lookup.$', !choice.isActive, isSuccessful);
            this.state.endSave();
        }
    }

}
